/**
 * WMSLayer
 *
 * Implementation for WMS (Web Map Service) raster layers.
 * WMS layers display tiled images from a WMS server.
 *
 * Expected config structure:
 * {
 *   "layers": "workspace:layername",      // WMS layer names (comma-separated)
 *   "styles": "default",                   // WMS styles (comma-separated, optional)
 *   "format": "image/png",                 // Image format
 *   "transparent": true,                   // Transparent background
 *   "version": "1.3.0",                    // WMS version (1.1.1 or 1.3.0)
 *   "crs": "EPSG:3857",                    // Coordinate reference system (optional)
 *   "tiled": true                          // Use tiled requests (optional)
 * }
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { substituteDateTokens } from './utils/substituteDateTokens';
import { clickToDevicePixels } from './utils/wmsPixelScale';
import { visibilityAwareInterval, VisibilityAwareIntervalHandle } from './utils/visibilityAwareInterval';
import { isInRefreshTimeRange } from './utils/refreshTimeRange';
import { LayerRegistry } from './LayerRegistry';
import { logger } from '../../utils/logger';
import { getBackendUrl } from '../../services/configService';
import './WMSLayer.scss';

const log = logger('WMSLayer');

export class WMSLayer extends BaseLayer {
  /**
   * Mapbox source ID for this layer
   */
  private sourceId: string;

  /**
   * Mapbox layer ID for this layer
   */
  private layerId: string;

  /**
   * Auto-refresh timer (visibility-aware). Created in onEnable when
   * `config.refreshInterval` is set, cancelled in onDisable / onDestroy.
   */
  private refreshHandle: VisibilityAwareIntervalHandle | null = null;

  /**
   * Constructor
   *
   * @param map Mapbox GL map instance
   * @param config Layer configuration from database
   */
  constructor(map: mapboxgl.Map, config: LayerConfig) {
    super(map, config);
    this.sourceId = `wms-source-${config.id}`;
    this.layerId = `wms-layer-${config.id}`;
  }

  /**
   * Initialize WMS layer
   */
  protected async onInitialize(): Promise<void> {
    // Validate required config
    if (!this.config.source_url) {
      throw new Error('WMS layer requires source_url');
    }

    if (!this.config.config.layers) {
      throw new Error('WMS layer requires config.layers');
    }

    // Validate WMS version if provided
    const version = this.config.config.version || '1.3.0';
    if (version !== '1.1.1' && version !== '1.3.0') {
      throw new Error('WMS version must be 1.1.1 or 1.3.0');
    }
  }

  /**
   * Enable WMS layer on map
   */
  protected async onEnable(): Promise<void> {
    // Build WMS URL template (includes &_cb=<key> when polling is configured)
    const wmsUrl = this.buildWMSUrl();

    log.debug(`onEnable: ${this.config.name}`);

    // Add source if it doesn't exist
    if (!this.map.getSource(this.sourceId)) {
      const sourceOptions: mapboxgl.RasterSourceSpecification = {
        type: 'raster',
        tiles: [wmsUrl],
        tileSize: this.config.config.tileSize || 512
      };

      // Only add bounds if it's defined and valid
      if (this.config.config.bounds && Array.isArray(this.config.config.bounds) && this.config.config.bounds.length === 4) {
        sourceOptions.bounds = this.config.config.bounds as [number, number, number, number];
      }

      this.map.addSource(this.sourceId, sourceOptions);
    } else {
      // Source exists from an earlier enable. If polling is active and the
      // cache-bust key has advanced (e.g. user toggled off in the morning
      // and back on after the 13:30 NWT fire-danger flip), update tile URLs
      // so Mapbox refetches. Otherwise the in-memory tile cache keeps
      // serving stale renders.
      const src = this.map.getSource(this.sourceId) as mapboxgl.RasterTileSource | undefined;
      const currentTiles = (src as any)?.tiles as string[] | undefined;
      if (src && currentTiles?.[0] !== wmsUrl) {
        src.setTiles([wmsUrl]);
        log.debug(`onEnable: tile URL changed, forced refresh via setTiles`);
      }
    }

    // Add layer if it doesn't exist
    if (!this.map.getLayer(this.layerId)) {
      const layerDef: mapboxgl.RasterLayer = {
        id: this.layerId,
        type: 'raster',
        source: this.sourceId,
        minzoom: this.config.zoom_min,
        maxzoom: this.config.zoom_max,
        paint: {
          'raster-opacity': this.state.currentOpacity,
          'raster-fade-duration': 300
        }
      };

      // Insert layer at appropriate position based on order
      const beforeId = this.findInsertBeforeLayer();
      if (beforeId) {
        this.map.addLayer(layerDef, beforeId);
      } else {
        this.map.addLayer(layerDef);
      }
    } else {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'visible');
    }

    // Start auto-refresh if configured. refreshInterval is required for any
    // polling — refreshTimeRange (optional) only gates which intervals fire.
    const refreshInterval = this.config.config.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      this.startAutoRefresh(refreshInterval);
      log.debug(`onEnable: auto-refresh started, every ${refreshInterval}ms`);
    }
  }

  /**
   * Disable WMS layer on map
   */
  protected async onDisable(): Promise<void> {
    this.stopAutoRefresh();
    if (this.map.getLayer(this.layerId)) {
      this.map.setLayoutProperty(this.layerId, 'visibility', 'none');
    }
  }

  /**
   * Update layer opacity
   *
   * @param opacity Opacity value (0-1)
   */
  protected async onSetOpacity(opacity: number): Promise<void> {
    if (this.map.getLayer(this.layerId)) {
      this.map.setPaintProperty(this.layerId, 'raster-opacity', opacity);
    }
  }

  /**
   * Update layer order
   *
   * @param order Order value (higher = on top)
   */
  protected onSetOrder(order: number): void {
    if (this.map.getLayer(this.layerId)) {
      // Move layer to appropriate position
      this.moveLayerToOrder(this.layerId);
    }
  }

  /**
   * Destroy layer and clean up resources
   */
  protected async onDestroy(): Promise<void> {
    this.stopAutoRefresh();

    // Remove layer
    if (this.map.getLayer(this.layerId)) {
      this.map.removeLayer(this.layerId);
    }

    // Remove source
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }
  }

  /**
   * Manually refresh by rebuilding the tile URL with the current cache-bust
   * key and calling setTiles. Called by the visibility-aware interval.
   * Respects `refreshTimeRange` — if the current NWT time is outside the
   * configured window, this is a no-op (timer keeps ticking but doesn't fetch).
   */
  private refreshTiles(): void {
    const range = this.config.config.refreshTimeRange;
    if (range && !isInRefreshTimeRange(range)) {
      log.debug(`refresh skipped — outside refreshTimeRange ${range}`);
      return;
    }
    const src = this.map.getSource(this.sourceId) as mapboxgl.RasterTileSource | undefined;
    if (!src) return;
    const newUrl = this.buildWMSUrl();
    src.setTiles([newUrl]);
    log.debug(`refresh: setTiles → ${newUrl.slice(0, 100)}…`);
  }

  /**
   * Start visibility-aware auto-refresh. Wrapper pauses on `document.hidden`
   * and fires one immediate refresh on hidden→visible transition.
   */
  private startAutoRefresh(intervalMs: number): void {
    this.stopAutoRefresh();
    this.refreshHandle = visibilityAwareInterval(() => this.refreshTiles(), intervalMs);
  }

  private stopAutoRefresh(): void {
    if (this.refreshHandle) {
      this.refreshHandle.cancel();
      this.refreshHandle = null;
    }
  }

  /**
   * Get Mapbox layer IDs
   */
  public getMapboxLayerIds(): string[] {
    return [this.layerId];
  }

  /**
   * Get Mapbox source IDs
   */
  public getMapboxSourceIds(): string[] {
    return [this.sourceId];
  }

  /**
   * Build WMS URL template for tile requests. Includes the current
   * cache-bust key when polling (refreshInterval) is configured, so each
   * polling tick produces a distinct URL Mapbox treats as a fresh source.
   *
   * @returns WMS URL template with {bbox-epsg-3857} placeholder
   */
  private buildWMSUrl(): string {
    return buildWMSUrlFromConfig(
      {
        source_url: this.config.source_url!,
        layers: this.config.config.layers,
        styles: this.config.config.styles,
        format: this.config.config.format,
        transparent: this.config.config.transparent,
        version: this.config.config.version,
        crs: this.config.config.crs,
        tiled: this.config.config.tiled,
        tileSize: this.config.config.tileSize,
        customParams: this.config.config.customParams,
        refreshInterval: this.config.config.refreshInterval,
      },
      getBackendUrl() || '',
    );
  }

  /**
   * Insert layer at appropriate position based on order
   *
   * @param layerDef Layer definition
   */
  private insertLayerAtOrder(layerDef: mapboxgl.RasterLayer): void {
    // Find the first layer with a higher order (if any)
    const beforeId = this.findInsertBeforeLayer();

    if (beforeId) {
      this.map.addLayer(layerDef, beforeId);
    } else {
      this.map.addLayer(layerDef);
    }
  }

  /**
   * Move existing layer to appropriate position based on order
   *
   * @param layerId Layer ID to move
   */
  private moveLayerToOrder(layerId: string): void {
    const beforeId = this.findInsertBeforeLayer();

    if (beforeId) {
      this.map.moveLayer(layerId, beforeId);
    } else {
      // Move to top (no beforeId means it should be on top)
      // Mapbox doesn't have a "move to top" method, so we need to find the topmost layer
      const layers = this.map.getStyle().layers;
      if (layers && layers.length > 0) {
        const topLayer = layers[layers.length - 1];
        if (topLayer.id !== layerId) {
          this.map.moveLayer(layerId, topLayer.id);
        }
      }
    }
  }

  /**
   * Find the layer ID to insert before based on order
   *
   * WMS layers are raster layers and can only be positioned relative to other
   * raster layers (WMS, TMS). They cannot be placed above symbol/vector layers
   * like WFS or GeoJSON due to Mapbox GL's rendering pipeline.
   *
   * Raster layers are always rendered below vector/symbol layers regardless
   * of the order specified in addLayer().
   *
   * @returns Layer ID to insert before, or undefined to insert on top of raster stack
   */
  private findInsertBeforeLayer(): string | undefined {
    try {
      const registry = LayerRegistry.getInstance(this.map);
      const thisOrder = this.state.currentOrder;

      // Only consider other RASTER layers (wms, tms) for positioning
      // WFS/GeoJSON are symbol layers and will always render above raster
      const rasterTypes = ['wms', 'tms'];

      // Get all enabled raster layers from registry
      const rasterLayers = registry.getAll().filter(layer => {
        const config = layer.getConfig();
        return layer.isEnabled() &&
               rasterTypes.includes(config.type) &&
               layer.getId() !== this.config.id;
      });

      // Find the first raster layer with higher order
      for (const layer of rasterLayers) {
        const otherOrder = layer.getOrder();

        if (otherOrder > thisOrder) {
          const layerIds = layer.getMapboxLayerIds();
          if (layerIds.length > 0) {
            const beforeId = layerIds[0];
            // Verify the layer actually exists on the map
            if (this.map.getLayer(beforeId)) {
              return beforeId;
            }
          }
        }
      }

      // If no raster layer found, insert below the first symbol/label layer
      // This ensures raster layers stay below vector content
      const mapLayers = this.map.getStyle().layers;
      if (mapLayers) {
        const firstSymbolLayer = mapLayers.find(layer =>
          layer.type === 'symbol'
        );
        if (firstSymbolLayer) {
          return firstSymbolLayer.id;
        }
      }

      return undefined; // Insert on top if no suitable layer found
    } catch (error) {
      log.warn('Error finding insert position, using fallback:', error);
      return undefined;
    }
  }

  /**
   * Build GetFeatureInfo URL for a click location.
   * Public so GetFeatureInfoCoordinator can use it.
   */
  public buildGetFeatureInfoUrl(lngLat: mapboxgl.LngLat, point: mapboxgl.Point): string {
    const backendUrl = getBackendUrl() || '';
    let baseUrl = this.config.source_url!;

    if (baseUrl.startsWith('/') && backendUrl) {
      baseUrl = backendUrl + baseUrl;
    }

    const config = this.config.config;
    const version = config.version || '1.3.0';
    const crs = config.crs || 'EPSG:3857';

    // Get current map bounds in the appropriate CRS
    const bounds = this.map.getBounds();
    if (!bounds) {
      throw new Error('Map bounds not available');
    }
    const canvas = this.map.getCanvas();
    const width = canvas.width;
    const height = canvas.height;

    // WIDTH/HEIGHT above are the canvas DEVICE-pixel dimensions (Mapbox scales the drawing
    // buffer by the device pixel ratio). `point` from the click event is in CSS pixels, so
    // I/J must be scaled into the same device-pixel space or the query resolves to the wrong
    // location on HiDPI/Retina displays (#652).
    const { i: deviceI, j: deviceJ } = clickToDevicePixels(
      { x: point.x, y: point.y },
      {
        width,
        height,
        clientWidth: canvas.clientWidth,
        clientHeight: canvas.clientHeight,
      },
    );

    // Calculate BBOX based on CRS
    let bbox: string;
    if (crs === 'EPSG:4326') {
      if (version === '1.3.0') {
        // WMS 1.3.0 with EPSG:4326 uses lat,lon order
        bbox = `${bounds.getSouth()},${bounds.getWest()},${bounds.getNorth()},${bounds.getEast()}`;
      } else {
        bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      }
    } else {
      // EPSG:3857 - convert to Web Mercator
      const sw = this.lngLatToMercator(bounds.getSouthWest());
      const ne = this.lngLatToMercator(bounds.getNorthEast());
      bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`;
    }

    const params = new URLSearchParams({
      SERVICE: 'WMS',
      VERSION: version,
      REQUEST: 'GetFeatureInfo',
      LAYERS: config.layers,
      QUERY_LAYERS: config.layers,
      STYLES: '', // Always empty for GetFeatureInfo - let GeoServer use default
      FORMAT: config.format || 'image/png',
      INFO_FORMAT: 'application/json',
      WIDTH: String(width),
      HEIGHT: String(height),
      BBOX: bbox,
      FEATURE_COUNT: '10',
      BUFFER: '15' // GeoServer vendor param: pixel radius for point/line tolerance
    });

    // Add CRS/SRS parameter
    if (version === '1.3.0') {
      params.append('CRS', crs);
      params.append('I', String(deviceI));
      params.append('J', String(deviceJ));
    } else {
      params.append('SRS', crs);
      params.append('X', String(deviceI));
      params.append('Y', String(deviceJ));
    }

    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${params.toString()}`;
  }

  /**
   * Convert LngLat to Web Mercator coordinates
   */
  private lngLatToMercator(lngLat: mapboxgl.LngLat): { x: number; y: number } {
    const R = 6378137; // Earth's radius in meters
    const x = R * lngLat.lng * Math.PI / 180;
    const y = R * Math.log(Math.tan(Math.PI / 4 + lngLat.lat * Math.PI / 360));
    return { x, y };
  }
}

/**
 * Build a WMS GetMap URL template from a flat config bag.
 *
 * Extracted as a free function so it's directly unit-testable without
 * instantiating WMSLayer (which needs a real mapboxgl.Map).
 */
interface WMSUrlInput {
  source_url: string;
  layers: string;
  styles?: string;
  format?: string;
  transparent?: boolean;
  version?: string;
  crs?: string;
  tiled?: boolean;
  /** Tile pixel size. Must match the Mapbox raster source tileSize so the
   *  GetMap WIDTH/HEIGHT line up with the requested tile (512 = 4x fewer tiles
   *  than 256, caching on GeoServer's WebMercatorQuadx2 gridset). */
  tileSize?: number;
  customParams?: Record<string, any>;
  /**
   * Refresh interval in ms. When set, the URL gets a `_cb=<key>` cache-bust
   * param that advances every `refreshInterval` ms. Mapbox keys tiles by URL,
   * so a new key forces a refetch. Without this, polling-driven refresh
   * silently does nothing for WMS layers.
   */
  refreshInterval?: number;
}

/**
 * Cache-bust key — `Math.floor(now / refreshInterval)`. Stable within each
 * tick window, advances every interval. Exported for tests.
 */
export function cacheBustKeyForInterval(refreshIntervalMs: number, now: number = Date.now()): string {
  if (!refreshIntervalMs || refreshIntervalMs <= 0) return '';
  return String(Math.floor(now / refreshIntervalMs));
}

export function buildWMSUrlFromConfig(input: WMSUrlInput, backendUrl: string): string {
  let baseUrl = substituteDateTokens(input.source_url);
  if (baseUrl.startsWith('/') && backendUrl) {
    baseUrl = backendUrl + baseUrl;
  }

  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: input.version || '1.3.0',
    REQUEST: 'GetMap',
    LAYERS: input.layers,
    STYLES: input.styles || '',
    FORMAT: input.format || 'image/png',
    TRANSPARENT: input.transparent !== false ? 'true' : 'false',
    WIDTH: String(input.tileSize || 512),
    HEIGHT: String(input.tileSize || 512),
  });

  const version = input.version || '1.3.0';
  const crs = input.crs || 'EPSG:3857';
  const bboxPlaceholder = crs === 'EPSG:4326' ? '{bbox-epsg-4326}' : '{bbox-epsg-3857}';

  if (version === '1.3.0') {
    params.append('CRS', crs);
  } else {
    params.append('SRS', crs);
  }

  // Emit TILED only when explicitly set. GeoServer treats TILED=true as a hint
  // to serve from the tile cache; absence is the historical default.
  if (input.tiled !== undefined) {
    params.append('TILED', input.tiled ? 'true' : 'false');
  }

  if (input.customParams) {
    for (const [key, value] of Object.entries(input.customParams)) {
      params.append(key, String(value));
    }
  }

  // Cache-bust param for polling-driven refresh. Only added when refreshInterval
  // is set; existing static WMS layers stay byte-identical.
  if (input.refreshInterval && input.refreshInterval > 0) {
    params.append('_cb', cacheBustKeyForInterval(input.refreshInterval));
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${params.toString()}&BBOX=${bboxPlaceholder}`;
}

/**
 * Test-only export — call directly from vitest with a config object.
 */
export const __buildWMSUrlForTests = (input: WMSUrlInput, backendUrl = ''): string =>
  buildWMSUrlFromConfig(input, backendUrl);
